import { useState, useRef, useEffect } from 'react';
import { Notify, Cell, Dialog, Button, Input, Collapse, CollapseItem, Radio, Checkbox, Divider } from '@nutui/nutui-react';
import { TeamManager } from '../teamConfig';
import { listPresetTeamSlots } from '../common';
import TeamDetail from './TeamDetail';
import TeamEditor from './TeamEditor';

/**
 * 阵容管理主组件
 * 包含阵容列表展示、操作栏（新建/复制/加载预设）、编辑弹窗和各种操作对话框
 */
function TeamSection({ user }) {
    // 数据状态
    const [displayTeams, setDisplayTeams] = useState([]);
    const [teamsLoading, setTeamsLoading] = useState(false);
    const teamManagerRef = useRef(null);

    // 编辑弹窗
    const [editorVisible, setEditorVisible] = useState(false);
    const [editingTeam, setEditingTeam] = useState(null); // null=新建, object=编辑

    // 复制弹窗
    const [copyDialogVisible, setCopyDialogVisible] = useState(false);
    const [copyTeamId, setCopyTeamId] = useState('');

    // 应用阵容弹窗
    const [applyDialogVisible, setApplyDialogVisible] = useState(false);
    const [applyTarget, setApplyTarget] = useState(null);
    const [applyPresetId, setApplyPresetId] = useState(0); // 0 表示未选中任何预设阵容槽位

    // 加载预设阵容选择弹窗
    const [loadPresetsDialogVisible, setLoadPresetsDialogVisible] = useState(false);
    const [selectedPresetIds, setSelectedPresetIds] = useState([]); // string[]

    // 操作状态
    const [isOperating, setIsOperating] = useState(false);
    const [loadingPresets, setLoadingPresets] = useState(false);
    const [applyingTeam, setApplyingTeam] = useState(false);
    const [activeCollapse, setActiveCollapse] = useState(null); // 当前展开的阵容ID

    // 应用阵容后的概览数据（按阵容ID存储）
    const [appliedOverviews, setAppliedOverviews] = useState({});

    // 应用阵容后本地记录的预设槽位最新名称（应用会修改游戏内预设阵容名称）
    const [presetNameOverrides, setPresetNameOverrides] = useState({}); // { [slotId]: name }

    const roleId = user?.role_info?.roleId;

    // 根据角色实际拥有的槽位和presetTeamInfo生成预设阵容列表
    const getPresetTeamList = () => {
        // text 为槽位默认名，name 为用户自定义名称
        const teamList = listPresetTeamSlots(user?.role_info).map(slot => ({
            value: slot.id,
            text: `阵容${slot.id}`,
            name: slot.name,
        }));

        // 应用“应用阵容”后本地记录的最新预设阵容名称，使槽位列表显示最新名称
        // 使用存在性检查（in），确保服务器返回空字符串时也能覆盖/清除旧名称
        for (const item of teamList) {
            if (item.value in presetNameOverrides) {
                item.name = presetNameOverrides[item.value];
            }
        }

        return teamList;
    };

    const presetTeamList = getPresetTeamList();

    // 生成用于“加载预设阵容”选择的选项列表，格式：阵容N（自定义名称）
    const getPresetTeamOptions = () => {
        // 角色实际可用的槽位数（VIP 只提供下限，活动兑换的布阵槽位可超出 VIP）
        const slots = listPresetTeamSlots(user?.role_info);
        let maxId = slots.length;

        // 收集用户自定义的阵容名称
        const nameMap = {};
        for (const slot of slots) {
            if (slot.name) nameMap[slot.id] = slot.name;
        }

        // 应用“应用阵容”后本地记录的最新预设阵容名称
        // 服务器返回空字符串时清除旧名称（表示该槽位无自定义名称）
        for (const [slotId, name] of Object.entries(presetNameOverrides)) {
            const id = parseInt(slotId, 10);
            if (id > maxId) maxId = id;
            if (name) {
                nameMap[id] = name;
            } else {
                delete nameMap[id];
            }
        }

        const options = [];
        for (let id = 1; id <= maxId; id++) {
            const customName = nameMap[id];
            options.push({
                value: id,
                label: customName ? `阵容${id}（${customName}）` : `阵容${id}`,
            });
        }
        return options;
    };

    const presetTeamOptions = getPresetTeamOptions();

    // 初始化和获取阵容数据
    useEffect(() => {
        const fetchTeams = async () => {
            if (!user.encrypt_user) return;
            setTeamsLoading(true);
            try {
                if (!teamManagerRef.current) {
                    teamManagerRef.current = new TeamManager();
                    await teamManagerRef.current.init();
                }
                await teamManagerRef.current.fetchTeams();
                setDisplayTeams(teamManagerRef.current.getDisplayTeams());
            } catch (error) {
                console.error('获取阵容数据失败:', error);
            } finally {
                setTeamsLoading(false);
            }
        };
        fetchTeams();
    }, [user.encrypt_user]);

    const refreshTeams = async () => {
        if (!teamManagerRef.current) return;
        try {
            await teamManagerRef.current.refreshTeams();
            setDisplayTeams(teamManagerRef.current.getDisplayTeams());
        } catch (error) {
            console.error('刷新阵容数据失败:', error);
        }
    };

    // ---- 新建阵容 ----
    const handleCreate = () => {
        setEditingTeam(null);
        setEditorVisible(true);
    };

    // ---- 编辑阵容 ----
    const handleEdit = team => {
        const rawTeam = teamManagerRef.current?.getTeamById(team.id);
        if (rawTeam) {
            setEditingTeam(rawTeam);
            setEditorVisible(true);
        }
    };

    // ---- 保存阵容（新建或编辑） ----
    const handleSave = async formData => {
        try {
            setIsOperating(true);
            if (formData.id) {
                await teamManagerRef.current.updateTeam(formData.id, formData);
                Notify.success('更新成功');
            } else {
                await teamManagerRef.current.createTeam(formData);
                Notify.success('创建成功');
            }
            await refreshTeams();
            setEditorVisible(false);
        } catch (error) {
            Notify.warn(error.message || '保存失败');
        } finally {
            setIsOperating(false);
        }
    };

    // ---- 删除阵容 ----
    const handleDelete = team => {
        Dialog.alert({
            title: '确认删除',
            content: `确定删除阵容 "${team.name}" 吗？`,
            confirmText: '删除',
            cancelText: '取消',
            hideCancelButton: false,
            onConfirm: async () => {
                try {
                    setIsOperating(true);
                    await teamManagerRef.current.deleteTeam(team.id);
                    Notify.success('删除成功');
                    await refreshTeams();
                } catch (error) {
                    Notify.warn(error.message || '删除失败');
                } finally {
                    setIsOperating(false);
                }
            },
        });
    };

    // ---- 复制阵容 ----
    const handleCopy = async () => {
        if (!copyTeamId.trim()) {
            Notify.warn('请输入阵容ID');
            return;
        }
        try {
            setIsOperating(true);
            await teamManagerRef.current.copyTeam(copyTeamId.trim());
            Notify.success('复制成功');
            await refreshTeams();
            setCopyDialogVisible(false);
            setCopyTeamId('');
        } catch (error) {
            Notify.warn(error.message || '复制失败');
        } finally {
            setIsOperating(false);
        }
    };

    // ---- 加载预设阵容 ----
    // 打开选择弹窗，默认全不选
    const handleLoadPresetsOpen = () => {
        setSelectedPresetIds([]);
        setLoadPresetsDialogVisible(true);
    };

    // 确认选择后，仅加载用户勾选的阵容；未选择任何阵容时直接关闭弹窗，不做任何操作
    const handleLoadPresetsConfirm = async () => {
        if (selectedPresetIds.length === 0) {
            setLoadPresetsDialogVisible(false);
            return;
        }
        setLoadPresetsDialogVisible(false);
        try {
            setLoadingPresets(true);
            const ids = selectedPresetIds.map(v => parseInt(v, 10));
            const teams = await teamManagerRef.current.loadPresets(ids);
            Notify.success(`成功加载 ${teams.length} 个预设阵容`);
            setDisplayTeams(teamManagerRef.current.getDisplayTeams());
        } catch (error) {
            Notify.warn(error.message || '加载预设阵容失败');
        } finally {
            setLoadingPresets(false);
        }
    };

    // ---- 应用阵容 ----
    const handleApplyOpen = team => {
        setApplyTarget(team);
        // 默认选中阵容保存的 teamId；若 teamId<1 或大于角色可用的最大预设阵容（不在 presetTeamList 内），则默认不选中
        const defaultPresetId =
            team.teamId && presetTeamList.some(t => t.value === team.teamId) ? team.teamId : 0;
        setApplyPresetId(defaultPresetId);
        setApplyDialogVisible(true);
    };

    const handleApplyConfirm = async () => {
        if (!applyTarget) return;
        // 防御：未选中任何预设阵容槽位时不允许应用
        if (!applyPresetId) {
            Notify.warn('请选择要应用到的预设阵容');
            return;
        }
        // 提前捕获目标阵容和槽位（finally 中会清空 applyTarget）
        const target = applyTarget;
        const targetPresetId = applyPresetId;
        setApplyDialogVisible(false);
        try {
            setApplyingTeam(true);
            setIsOperating(true);
            const { overview, presetTeamName } = await teamManagerRef.current.applyTeam(
                target.id,
                targetPresetId
            );
            // 存储应用后的概览数据
            setAppliedOverviews(prev => ({
                ...prev,
                [target.id]: overview,
            }));
            // 使用服务器返回的权威预设阵容名称更新槽位列表。
            // 客户端无法确定服务器是否设置成功（如违禁关键字会被拒绝），以服务器返回值为准
            setPresetNameOverrides(prev => ({
                ...prev,
                [targetPresetId]: presetTeamName,
            }));
            Notify.success('阵容应用成功，请在下方查看当前阵容概览');
        } catch (error) {
            Notify.warn(error.message || '应用阵容失败');
        } finally {
            setApplyingTeam(false);
            setIsOperating(false);
            setApplyTarget(null);
        }
    };

    const configEnums = teamManagerRef.current?.getConfigEnums();

    return (
        <>
            {/* 操作栏 */}
            <Cell style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                    <Button size='small' type='default' onClick={handleCreate} disabled={isOperating}>
                        新建阵容
                    </Button>
                    <Button
                        size='small'
                        type='default'
                        onClick={() => setCopyDialogVisible(true)}
                        disabled={isOperating}>
                        复制阵容
                    </Button>
                    <Button
                        size='small'
                        type='default'
                        loading={loadingPresets}
                        onClick={handleLoadPresetsOpen}
                        disabled={isOperating || loadingPresets}>
                        {loadingPresets ? '加载中...' : '加载预设阵容（需要上号）'}
                    </Button>
                    <Button
                        size='small'
                        type='info'
                        onClick={() => {
                            Dialog.alert({
                                title: '阵容配置使用说明',
                                content: (
                                    <div style={{ fontSize: '13px', lineHeight: '1.8', textAlign: 'left' }}>
                                        <h4 style={{ margin: '0 0 8px 0', color: '#1890ff' }}>设计目的</h4>
                                        <p style={{ margin: '0 0 12px 0', textIndent: '2em' }}>
                                            使用脚本的阵容配置可以为一个角色保存无限多个战斗阵容，不受游戏中预设阵容个数的限制。目前支持的阵容设置：玩具、咸将、装备（洗练+水晶）、鱼灵（绑定鱼珠洗练）、鱼珠技能。目前不支持的阵容设置：咸将等级、俱乐部科技。玩具只会在现有的玩具之间进行切换，不会使用“无损转化”功能。暂不支持洗练、水晶自动反面功能。目前可以从当前页面手工切换/应用阵容，脚本在执行日常任务时也可以修改指定的预设阵容为脚本保存的阵容。
                                        </p>

                                        <h4 style={{ margin: '0 0 8px 0', color: '#1890ff' }}>一、装备优先级</h4>
                                        <p style={{ margin: '0 0 12px 0', textIndent: '2em' }}>
                                            在更换阵容时，系统需要确定每个咸将应该穿戴哪一套装备。"装备优先级"数值越小，越优先为该位置选择装备。系统会按照优先级从低到高的顺序，依次为每个出战位置查找最合适的装备。
                                        </p>
                                        <p style={{ margin: '0 0 12px 0', textIndent: '2em', color: '#666' }}>
                                            例如：位置1设置优先级为0，位置2设置优先级为1。那么系统会先为位置1选择装备，选中的装备将被标记为"已占用"，然后再为位置2从剩余装备中选择。
                                        </p>

                                        <h4 style={{ margin: '0 0 8px 0', color: '#1890ff' }}>二、关键属性</h4>
                                        <p style={{ margin: '0 0 8px 0', textIndent: '2em' }}>
                                            "关键属性"用于描述某个咸将最看重哪些装备洗练属性。系统会在所有咸将中，找出4件装备加上水晶的关键属性总和最高的那套装备，分配给该咸将。关键属性不计算金鱼属性、鱼珠洗练。
                                        </p>
                                        <p style={{ margin: '0 0 8px 0', textIndent: '2em', color: '#666' }}>
                                            <strong>为什么使用关键属性而不是通过洗练指定装备？</strong><br/>
                                            使用关键属性，无论怎么改洗练，系统都能找到最适合该咸将的那套装备。
                                        </p>
                                        <p style={{ margin: '0 0 12px 0', textIndent: '2em', color: '#666' }}>
                                            <strong>举例：</strong>优先级1是吕布，设置关键属性为"攻击、暴击"；优先级2是诸葛亮，设置关键属性为"技能伤害、攻击"。应用阵容时，系统会自动找出攻击+暴击属性最高的装备给吕布，技能伤害+攻击最高的装备给诸葛亮。
                                        </p>

                                        <h4 style={{ margin: '0 0 8px 0', color: '#1890ff' }}>三、绑定装备</h4>
                                        <p style={{ margin: '0 0 12px 0', textIndent: '2em' }}>
                                            除了使用"关键属性"来自动匹配装备外，还可以选择"绑定装备"方式。绑定装备会记住每个咸将当前穿戴的那一套装备（包括洗练和水晶），切换阵容时直接还原这套装备，不再根据属性重新匹配。
                                        </p>
                                        <p style={{ margin: '0 0 12px 0', textIndent: '2em' }}>
                                            要使用绑定装备功能，需要先通过"加载预设阵容"按钮从游戏中获取当前的装备信息。只有加载过的阵容才能开启绑定装备。
                                        </p>
                                        <p style={{ margin: '0 0 12px 0', textIndent: '2em', color: '#666' }}>
                                            <strong>绑定装备的好处：</strong>切换阵容时装备分配更精确，不会因为多个咸将属性相近而导致装备分配错误。
                                        </p>
                                        <p style={{ margin: '0 0 12px 0', textIndent: '2em', color: '#666' }}>
                                            <strong>注意：</strong>如果您更换了装备的洗练属性或水晶，绑定不会失效，仍然会找到同一套装备。但如果装备本身被替换（比如用了更高级的装备），则可能需要重新加载预设阵容来更新绑定信息。复制其他角色的阵容时，装备绑定会被自动取消，因为不同角色的装备信息不通用，需要在自己的角色上重新加载预设阵容来绑定装备。
                                        </p>

                                        <h4 style={{ margin: '0 0 8px 0', color: '#1890ff' }}>四、其他设置说明</h4>
                                        <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px' }}>
                                            <li><strong>咸将最低/最高等级</strong>：限制可以选择的咸将等级范围</li>
                                            <li><strong>装备最低等级</strong>：只有4件装备全部达到此等级的咸将才会被选中</li>
                                            <li><strong>鱼灵</strong>：选择"不操作"则保持现状，选择"卸载"则取下鱼灵</li>
                                            <li><strong>鱼珠技能</strong>：为佩戴鱼灵的咸将设置灵珠技能</li>
                                        </ul>

                                        <h4 style={{ margin: '0 0 8px 0', color: '#1890ff' }}>五、配置建议</h4>
                                        <ul style={{ margin: '0', paddingLeft: '20px' }}>
                                            <li>优先级相同的两个位置，按位置编号(1-5)从小到大处理</li>
                                            <li>如果不确定关键属性，可以不设置，系统会按装备等级之和选择</li>
                                        </ul>
                                    </div>
                                ),
                                confirmText: '我知道了',
                                hideCancelButton: true,
                            });
                        }}>
                        使用说明
                    </Button>
                </div>

                {/* 阵容列表 */}
                {teamsLoading ? (
                    <div style={{ width: '100%', textAlign: 'center', padding: '20px' }}>加载中...</div>
                ) : displayTeams.length === 0 ? (
                    <div style={{ width: '100%', textAlign: 'center', padding: '20px', color: '#999' }}>
                        暂无可用阵容
                    </div>
                ) : (
                    <>
                        <style>{`
                            .nut-collapse-item-header { padding: 4px 4px !important; }
                        `}</style>
                        <Collapse
                            style={{ width: '100%' }}
                            value={activeCollapse ? [activeCollapse] : []}
                            onChange={(val) => setActiveCollapse(val.length > 0 ? val[val.length - 1] : null)}
                        >
                        {[...displayTeams].sort((a, b) => {
                            /* eslint-disable no-undef */
                            const diff = BigInt(b.id) - BigInt(a.id);
                            /* eslint-enable no-undef */
                            return diff > 0n ? 1 : diff < 0n ? -1 : 0; // BigInt比较结果转换为数字
                        }).map((team, index, arr) => (
                            <div key={team.id}>
                                <CollapseItem
                                    name={team.id}
                                    style={{ marginBottom: '0' }}
                                    title={
                                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                                            <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{team.name}</span>
                                            {team.presetTeamName && (
                                                <span style={{ fontSize: '12px', color: '#1890ff' }}>
                                                    <span style={{ whiteSpace: 'nowrap' }}>（{team.presetTeamName}）</span>
                                                </span>
                                            )}
                                            {team.public && (
                                                <div>
                                                    <span style={{ fontSize: '12px', color: '#52c41a', whiteSpace: 'nowrap' }}>[公共]</span>
                                                    <span
                                                        style={{ cursor: 'pointer', marginLeft: '4px', whiteSpace: 'nowrap' }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const copyToClipboard = (text) => {
                                                                if (navigator.clipboard && window.isSecureContext) {
                                                                    return navigator.clipboard.writeText(text);
                                                                } else {
                                                                    // fallback for non-HTTPS
                                                                    const textArea = document.createElement('textarea');
                                                                    textArea.value = text;
                                                                    textArea.style.position = 'fixed';
                                                                    textArea.style.left = '-999999px';
                                                                    document.body.appendChild(textArea);
                                                                    textArea.select();
                                                                    try {
                                                                        document.execCommand('copy');
                                                                        document.body.removeChild(textArea);
                                                                        return Promise.resolve();
                                                                    } catch (err) {
                                                                        document.body.removeChild(textArea);
                                                                        return Promise.reject(err);
                                                                    }
                                                                }
                                                            };
                                                            copyToClipboard(team.id).then(() => {
                                                                Notify.success('阵容ID已复制到剪贴板');
                                                            });
                                                        }}
                                                        title="点击复制ID"
                                                    >
                                                        复制ID🗐
                                                    </span>
                                                </div>
                                            )}
                                            {/* 操作按钮直接放在标题行，无需展开阵容详情即可操作 */}
                                            <div
                                                style={{ marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {team.roleId === roleId && (
                                                    <Button
                                                        size='small'
                                                        type='primary'
                                                        onClick={() => handleEdit(team)}
                                                        disabled={isOperating || applyingTeam}>
                                                        编辑
                                                    </Button>
                                                )}
                                                {team.roleId === roleId && (
                                                    <Button
                                                        size='small'
                                                        type='danger'
                                                        onClick={() => handleDelete(team)}
                                                        disabled={isOperating || applyingTeam}>
                                                        删除
                                                    </Button>
                                                )}
                                                <Button
                                                    size='small'
                                                    type='warning'
                                                    onClick={() => handleApplyOpen(team)}
                                                    disabled={isOperating || applyingTeam}>
                                                    应用阵容
                                                </Button>
                                            </div>
                                        </div>
                                    }>
                                    <TeamDetail
                                        team={team}
                                        appliedOverview={appliedOverviews[team.id]}
                                    />
                                </CollapseItem>
                                <Divider />
                            </div>
                        ))}
                        </Collapse>
                    </>
                )}
            </Cell>

            {/* 编辑/新建弹窗 */}
            <TeamEditor
                visible={editorVisible}
                onClose={() => setEditorVisible(false)}
                onSave={handleSave}
                team={editingTeam}
                configEnums={configEnums}
                presetTeamList={presetTeamList}
            />

            {/* 复制阵容对话框 */}
            <Dialog
                title='复制阵容'
                visible={copyDialogVisible}
                confirmText='复制'
                cancelText='取消'
                hideCancelButton={false}
                onConfirm={handleCopy}
                onCancel={() => {
                    setCopyDialogVisible(false);
                    setCopyTeamId('');
                }}
                closeOnOverlayClick={false}>
                <div style={{ padding: '10px 0' }}>
                    <div style={{ marginBottom: '8px', color: '#666', fontSize: '14px' }}>请输入要复制的阵容ID：</div>
                    <Input placeholder='阵容ID' value={copyTeamId} onChange={val => setCopyTeamId(val)} />
                </div>
            </Dialog>

            {/* 应用阵容对话框 */}
            <Dialog
                title='应用阵容'
                visible={applyDialogVisible}
                confirmText={applyingTeam ? '应用中...' : '确认应用'}
                cancelText='取消'
                hideCancelButton={false}
                onConfirm={handleApplyConfirm}
                onCancel={() => {
                    setApplyDialogVisible(false);
                    setApplyTarget(null);
                }}
                closeOnOverlayClick={false}>
                <div style={{ padding: '10px 0' }}>
                    <div style={{ marginBottom: '8px', color: '#666', fontSize: '14px' }}>
                        将阵容 &quot;{applyTarget?.name}&quot; 应用到预设阵容槽位：
                    </div>
                    <Radio.Group value={String(applyPresetId)} onChange={val => setApplyPresetId(parseInt(val))}>
                        {presetTeamList.map(team => (
                            <Radio key={team.value} value={String(team.value)} style={{ marginBottom: '4px' }}>
                                {team.name ? `阵容${team.value}：${team.name}` : `阵容${team.value}`}
                            </Radio>
                        ))}
                    </Radio.Group>
                    <div style={{ marginTop: '8px', color: '#ff8c00', fontSize: '12px' }}>
                        此操作需要登录游戏服务器，耗时约10-30秒
                    </div>
                </div>
            </Dialog>

            {/* 加载预设阵容选择对话框 */}
            <Dialog
                title='加载预设阵容'
                visible={loadPresetsDialogVisible}
                confirmText={loadingPresets ? '加载中...' : '确认加载'}
                cancelText='取消'
                hideCancelButton={false}
                onConfirm={handleLoadPresetsConfirm}
                onCancel={() => setLoadPresetsDialogVisible(false)}
                closeOnOverlayClick={false}>
                <div style={{ padding: '10px 0' }}>
                    <div style={{ marginBottom: '8px', color: '#666', fontSize: '14px' }}>
                        请选择需要加载并保存的阵容：
                    </div>
                    <Checkbox.Group
                        value={selectedPresetIds}
                        onChange={val => setSelectedPresetIds(val)}>
                        {presetTeamOptions.map(option => (
                            <Checkbox
                                key={option.value}
                                value={String(option.value)}
                                style={{ marginBottom: '4px' }}>
                                {option.label}
                            </Checkbox>
                        ))}
                    </Checkbox.Group>
                    <div style={{ marginTop: '8px', color: '#ff8c00', fontSize: '12px' }}>
                        此操作需要登录游戏服务器，耗时约10-30秒。仅会保存所选阵容，避免产生多余的重复阵容。
                    </div>
                </div>
            </Dialog>
        </>
    );
}

export default TeamSection;
